import React, { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Users,
  Car,
  CalendarRange,
  CreditCard,
  ReceiptText,
  FileSignature,
  MessageSquare,
  BarChart3,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/vehicles", label: "Vehicles", icon: Car },
  { to: "/admin/reservations", label: "Reservations", icon: CalendarRange },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/tolls", label: "Tolls / Tickets", icon: ReceiptText },
  { to: "/admin/agreements", label: "Agreements", icon: FileSignature },
  { to: "/admin/sms", label: "SMS Templates", icon: MessageSquare },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex flex-col h-full bg-gray-50 border-r border-gray-200">
      <div className="p-5 border-b border-gray-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-black text-white grid place-items-center">
          <Car size={18} />
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-base tracking-tight">Fair Rideshare</div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">CRM</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-black text-white" : "text-gray-700 hover:bg-gray-100"
              }`
            }
          >
            <item.icon size={16} strokeWidth={2} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-gray-200 space-y-2">
        <div className="px-3 py-2 rounded-md bg-white border border-gray-200">
          <div className="text-xs text-gray-500">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest mt-0.5 text-gray-500">{user?.role}</div>
        </div>
        <Button
          variant="outline"
          data-testid="logout-button"
          onClick={async () => {
            await logout();
            nav("/login");
          }}
          className="w-full justify-start"
        >
          <LogOut size={14} className="mr-2" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const current = navItems.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-gray-200 flex items-center justify-between px-4 h-14">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" className="-ml-2">
                {open ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="font-heading font-semibold text-sm">{current?.label || "Fair Rideshare"}</div>
          <div className="w-9" />
        </header>

        <main className="flex-1 has-bottom-nav">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
